import { z } from 'zod';
import { addressSchema, moneyStringSchema, uuidSchema } from './common';

export const orderItemInputSchema = z.object({
  productId: uuidSchema,
  // Set when buying a specific product variation (house or merchant); omitted
  // for single-SKU products.
  variantId: uuidSchema.optional(),
  qty: z.number().int().positive(),
  // A creator subscription plan line the buyer wants renewed automatically.
  // Honoured only on a cash WALLET checkout (the one way we can charge again
  // without the buyer present); every other line and method ignores it.
  autoRenew: z.boolean().optional(),
});

export const createOrderSchema = z.object({
  type: z.enum(['ecom', 'pos', 'slot_activation']),
  branchId: uuidSchema.optional(),
  items: z.array(orderItemInputSchema).min(1),
  notes: z.string().max(500).optional(),
  // The member's chosen slot for THIS order (attribution + comp/PV + pricing +
  // gc/coin wallet debit). The server validates ownership and falls back to the
  // earliest active slot if omitted or not one of the caller's active slots.
  buyerSlotId: uuidSchema.optional(),
  // Ecom shipping selection (resolved against a prior /storefront/shipping/quote).
  courierId: z.string().max(64).optional(),
  shippingFee: moneyStringSchema.optional(),
  shipToAddress: addressSchema.optional(),
  // Ecom fulfillment: 'ship' (courier, default) or 'branch_pickup' (collect at
  // the chosen `branchId`; no shipping, no address).
  fulfillmentMode: z.enum(['ship', 'branch_pickup']).optional(),
  // BF4: payment currency. 'cash' (default) follows the manual-payment flow;
  // 'gc'/'coin' debit the member's active slot wallet and settle instantly.
  // (Legacy + POS still pass `currency` directly; ecom prefers `paymentMethodCode`.)
  currency: z.enum(['cash', 'gc', 'coin']).optional(),
  // Ecom: code of an enabled entry in
  // `tenant_config.features.ecom_payment_methods.methods[]`. When present the
  // route derives currency + settlement from the registry (`currency` body
  // field is ignored). Required for /checkout submissions; older callers
  // (POS, slot-activation, public-checkout) may omit it.
  paymentMethodCode: z.string().min(1).max(64).optional(),
  // Coupon code (cash orders only). Applies a per-line discount to the
  // admin-selected eligible products. Ignored/rejected on non-cash orders.
  couponCode: z.string().min(1).max(64).optional(),
  // Single-payment correlation: a mixed cart stamps the house order and every
  // merchant order with the same client-minted id so the buyer can pay once.
  checkoutGroupId: uuidSchema.optional(),
});

/**
 * Body for POST /storefront/dropship-orders — an authenticated buyer (a lead
 * just signed up via the link, or an existing member) placing a retail dropship
 * order through a member's per-product replicated link. `ref` is the link's
 * referral code (plaintext or encrypted `e1.` token); the route resolves it to
 * the SHARER's earning slot. Always full-SRP, cash, manual settlement — no
 * coupon, no payment-method registry.
 */
export const createDropshipOrderSchema = z.object({
  items: z.array(orderItemInputSchema).min(1),
  ref: z.string().min(1).max(256),
  shipToAddress: addressSchema.optional(),
  notes: z.string().max(500).optional(),
  // The manual payment method the buyer chose at checkout. Falls back to the
  // first enabled manual method server-side when omitted/invalid.
  paymentMethodCode: z.string().min(1).max(64).optional(),
  // Single-payment correlation: when a dropship cart also contains merchant
  // items, the house dropship order + the merchant orders share this id so the
  // buyer pays once.
  checkoutGroupId: uuidSchema.optional(),
});

/**
 * Body for POST /storefront/shipping/quote — cart + origin + destination.
 * Origin is either a house POO `branchId` OR a `merchantUserId` (the merchant's
 * pickup region is the origin). Exactly one must be supplied.
 */
export const shippingQuoteSchema = z
  .object({
    branchId: uuidSchema.optional(),
    // Quote a merchant shop's items shipped from the merchant's pickup address.
    merchantUserId: uuidSchema.optional(),
    items: z.array(orderItemInputSchema).min(1),
    // Either pass an explicit address or rely on the caller's saved profile address.
    address: addressSchema.optional(),
  })
  .refine((v) => Boolean(v.branchId) !== Boolean(v.merchantUserId), {
    message: 'exactly one of branchId or merchantUserId is required',
  });

export const orderStatusSchema = z.enum([
  'pending',
  'to_pay',
  'paid',
  'to_pick',
  'to_ship',
  'shipping',
  'delivered',
  'to_release',
  'completed',
  'cancelled',
  'refunded',
]);

export const updateOrderStatusSchema = z.object({
  status: orderStatusSchema,
  note: z.string().max(500).optional(),
});

/** Body for POST /admin/orders/:id/ship — captures the courier tracking #. The
 * clickable tracking URL is derived from the order's courier template, not sent. */
export const shipOrderSchema = z.object({
  trackingNumber: z.string().min(1).max(120),
  note: z.string().max(500).optional(),
});

/** Body for POST /admin/orders/:id/deliver — admin marks the order delivered. */
export const deliverOrderSchema = z.object({
  note: z.string().max(500).optional(),
});

/** Body for POST /orders/:id/receive — member confirms receipt. */
export const receiveOrderSchema = z.object({
  note: z.string().max(500).optional(),
});

export const posPaymentMethodSchema = z.enum(['cash', 'card', 'wallet']);

export const posCheckoutSchema = z.object({
  branchId: uuidSchema,
  buyerUserId: uuidSchema,
  buyerSlotId: uuidSchema.nullish(),
  items: z.array(orderItemInputSchema).min(1),
  payment: z.object({
    method: posPaymentMethodSchema,
    amountTendered: moneyStringSchema,
    // BF4: pay in cash (default) or debit the buyer slot's gc/coin wallet.
    currency: z.enum(['cash', 'gc', 'coin']).optional(),
    // F1 CMS: the `pos_payment_methods` registry code the cashier picked. The
    // route resolves its label for `pos_payments.method_code`/`method_label`;
    // `method`/`currency` above still drive the tender/change math. Optional so
    // legacy clients keep working.
    paymentMethodCode: z.string().min(1).max(64).optional(),
    idempotencyKey: z.string().min(8).max(128).optional(),
    metadata: z.record(z.unknown()).optional(),
  }),
  notes: z.string().max(500).optional(),
  // QoL Slice 5b: optional order-level cashier manager discount. The route
  // checks the tenant cap (max_pct_without_approval / max_amount_without_approval)
  // and requires `approvalChip` (a short-lived signed token issued by
  // `POST /pos/discount-override/verify`) when either cap is exceeded.
  managerDiscount: z
    .object({
      pct: z.number().min(0).max(100).optional(),
      amount: z.number().min(0).optional(),
      reason: z.string().max(500).optional(),
      approvalChip: z.string().min(1).max(4096).optional(),
    })
    .optional(),
  // A preset the cashier pressed (`GET /pos/discount-presets`), by id. The route
  // resolves it to the coupon behind it; the till never needs a preset's code.
  discountPresetId: uuidSchema.optional(),
  // A code the customer handed over. One coupon per order, so this and
  // `discountPresetId` are mutually exclusive (enforced by the route, which
  // can answer with a code the till understands).
  couponCode: z.string().trim().min(1).max(64).optional(),
  // The manager approval a preset marked "needs a manager PIN" requires. Same
  // chip `/pos/discount-override/verify` issues for an above-cap discount.
  couponApprovalChip: z.string().min(1).max(4096).optional(),
  // A gift check of the buyer's (cashier phase 4). The third discount
  // instrument: never together with `discountPresetId` or `couponCode`.
  voucherId: uuidSchema.optional(),
  // Ad-hoc charge the cashier types at checkout (e.g. "Delivery" ₱50). Added on
  // top of the discounted goods total, never discounted, never PV-bearing —
  // same treatment as an ecom shipping fee. Positive-only: a negative here
  // would be an unaudited discount, which is what `managerDiscount` (capped +
  // manager-PIN-gated) exists for.
  customFee: z
    .object({
      label: z.string().trim().min(1).max(60),
      amount: moneyStringSchema.refine(
        (v) => Number(v) > 0,
        'custom fee must be greater than zero',
      ),
    })
    .optional(),
  /**
   * The registry-backed replacement for `customFee` above: several fees per
   * sale, each naming an admin-configured TYPE rather than carrying a label the
   * cashier typed. The server resolves the label from the code, which is what
   * makes "Delvery" unrepresentable.
   *
   * One line per type: two identical "Delivery" rows on a tape are unauditable,
   * and a second instance of the same fee is a quantity, not a type. Capped at
   * 10 — a receipt has to fit on the roll.
   *
   * `customFee` is kept and DEPRECATED rather than removed so a cashier tab
   * still running the previous bundle doesn't start 400ing mid-shift while a
   * deploy rolls. Drop it in a later contract-only change.
   */
  customFees: z
    .array(
      z.object({
        typeCode: z.string().trim().min(1).max(64),
        /**
         * OPTIONAL, because a `percent`/`flat` fee type is priced by the server
         * from the registry — the till never sends its amount, and the server
         * REJECTS one that does rather than ignoring it (`custom_fee_amount_not_accepted`),
         * so a client can never talk its own tax down. Required for a `manual`
         * type, where the cashier's typed amount is the only source; that is
         * enforced in `services/pos.ts`, which is the layer that knows each
         * type's calc.
         */
        amount: moneyStringSchema
          .refine((v) => Number(v) > 0, 'custom fee must be greater than zero')
          .optional(),
      }),
    )
    .max(10)
    .refine(
      (fs) => new Set(fs.map((f) => f.typeCode)).size === fs.length,
      'each custom fee type can only be added once',
    )
    .optional(),
  // "Sell to Stockist" mode. When true the sale is a stock TRANSFER into the
  // buyer slot's own stockist branch (no PV / comp / Code+PIN) and the slot must
  // own a stockist branch, else the checkout fails. Default false = normal sale.
  stockistTransfer: z.boolean().optional().default(false),
});
